import { useEffect, useRef, useState } from "react";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import type { FoodItem } from "@/components/FoodCard";
import { Camera as CameraIcon, Image as ImageIcon, X } from "@phosphor-icons/react";
import { SHARE_STATUSES, type ShareStatus } from "@/lib/shareStatus";
import { renderCheckIn } from "@/lib/shareCardCheckIn";
import { renderVerdict } from "@/lib/shareCardVerdict";
import { dispatchShare } from "@/lib/shareCardHelpers";
import { openInstagramStories } from "@/lib/shareTargets";
import { Capacitor } from "@capacitor/core";
import { fmtErr, isUserCancellation } from "@/lib/utils";
import { tap } from "@/lib/haptics";

const isNativeIOS = (() => {
  try { return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios"; } catch { return false; }
})();

/**
 * Yumz share cards — v2 (Wave C).
 *
 * Two distinct flows, each collapsed to a 2-step interaction:
 *  - Flow A "I'm here now"     → AnticipationShareCard (status picker → instant share)
 *  - Flow B "I ate this"        → VerdictShareCard      (rating picker → instant share)
 *
 * Both render via the v2 renderers in `src/lib/shareCard{CheckIn,Verdict}.ts`.
 * v2 is ON by default; set `localStorage.yumzCardsV2 = "0"` to disable for
 * emergency rollback (the renderer call is wrapped in try/catch and surfaces
 * a toast on error — the user can still cancel and retry).
 *
 * The legacy MOOD_TAGS (8 occasion keys) and 3-rating verdict system have
 * been retired in favor of the unified 7-status table (fire, vibes, honest,
 * mid, elite, cozy, lowkey) in `src/lib/shareStatus.ts`.
 */

// v2-default flag check (opt-out — set localStorage.yumzCardsV2 = "0" to disable)
function v2Enabled(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage?.getItem("yumzCardsV2") !== "0";
  } catch {
    return true;
  }
}

// Ordered list of statuses for the picker UI. Order chosen for visual flow:
// loud → atmospheric → critical → flat → premium → warm → understated.
const PICKER_ORDER: ShareStatus[] = [
  "fire",
  "vibes",
  "honest",
  "mid",
  "elite",
  "cozy",
  "lowkey",
];

// ─── Flow A: Anticipation / Check-in ────────────────────────────────────────────

interface AnticipationProps {
  restaurantName: string;
  /** Legacy prop — pre-selected mood, accepted for backward compat. */
  mood?: string | null;
  orderedDishes: FoodItem[];
  onClose: () => void;
  locationLabel?: string;
  /** Legacy callback — fires when a status is chosen. */
  onMoodSelected?: (status: ShareStatus) => void;
}

export type MoodTag = ShareStatus;
// Re-export for callers that imported MoodTag previously.
export { type ShareStatus };
// Stub for any legacy callers iterating MOOD_TAGS — exposes the 7 statuses
// in the same shape the old code expected. The legacy `gradient` array is
// preserved for backward compat (was [primaryColor, secondaryColor]); new
// callers should prefer `color`.
export const MOOD_TAGS = PICKER_ORDER.map((k) => {
  const def = SHARE_STATUSES[k];
  return {
    key: k,
    label: def.label,
    emoji: def.emoji,
    color: def.palette.accent,
    gradient: [def.palette.accent, def.palette.bg2] as [string, string],
  };
}) as readonly {
  key: ShareStatus;
  label: string;
  emoji: string;
  color: string;
  gradient: [string, string];
}[];

export function AnticipationShareCard({
  restaurantName,
  mood,
  onClose,
  locationLabel,
  onMoodSelected,
}: AnticipationProps) {
  const [selectedStatus, setSelectedStatus] = useState<ShareStatus | null>(
    (mood as ShareStatus) || null,
  );
  const [photo, setPhoto] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  // Generate preview Blob as soon as a status is picked. Live-update on
  // photo add. The same blob is what we'll dispatch on Share.
  useEffect(() => {
    if (!selectedStatus) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    let priorObjectUrl: string | null = null;
    (async () => {
      try {
        const blob = await renderCheckIn({
          restaurantName,
          locationLabel,
          status: selectedStatus,
          photoUrl: photo || undefined,
        });
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        priorObjectUrl = url;
        setPreviewUrl(url);
      } catch (e) {
        if (!cancelled) {
          console.error("[v2 check-in preview] failed:", fmtErr(e));
        }
      }
    })();
    return () => {
      cancelled = true;
      if (priorObjectUrl) {
        // small delay so the <img> can paint before we revoke
        setTimeout(() => URL.revokeObjectURL(priorObjectUrl!), 1000);
      }
    };
  }, [selectedStatus, photo, restaurantName, locationLabel]);

  const pickPhoto = async () => {
    try {
      const result = await Camera.getPhoto({
        quality: 80,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
        width: 1080,
        height: 1080,
      });
      if (result.dataUrl) setPhoto(result.dataUrl);
    } catch {
      // User cancelled.
    }
  };

  const takePhoto = async () => {
    try {
      const result = await Camera.getPhoto({
        quality: 80,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: 1080,
        height: 1080,
      });
      if (result.dataUrl) setPhoto(result.dataUrl);
    } catch {
      // User cancelled.
    }
  };

  const handleShare = async () => {
    if (!selectedStatus) {
      toast("pick a vibe first ✌️");
      return;
    }
    if (!v2Enabled()) {
      toast.error("v2 share disabled — set localStorage.yumzCardsV2 = '1' to re-enable", { duration: 4000 });
      return;
    }
    setSharing(true);
    try {
      onMoodSelected?.(selectedStatus);
      const blob = await renderCheckIn({
        restaurantName,
        locationLabel,
        status: selectedStatus,
        photoUrl: photo || undefined,
      });
      const def = SHARE_STATUSES[selectedStatus];
      const locSuffix = locationLabel ? ` in ${locationLabel}` : "";
      // Wave L1 — append a tap-back URL on its own line for clean iMessage /
      // WhatsApp previews. No per-check-in server record yet (Wave M will
      // add shared_checkin); for now the generic app URL gets recipients
      // into Yumz where they can scan their own menus.
      // Wave L2 — frame the URL as a clear CTA for non-users instead of a
      // bare link. Reads as marketing copy in iMessage / WhatsApp previews.
      const shareText = `at ${restaurantName}${locSuffix} ${def.emoji}\nGet Yumz: https://app.yumz.social`;
      await dispatchShare(blob, shareText, "yumz-moment");
      toast.success("shared! 🎉");
      track("moment1_shared", {
        restaurant: restaurantName,
        status: selectedStatus,
        hasPhoto: !!photo,
        hasLocation: !!locationLabel,
        v2: true,
      });
      onClose();
    } catch (e) {
      if (isUserCancellation(e)) return;
      console.error("[v2 check-in share] failed:", fmtErr(e));
      toast.error("share failed — try again");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col page-fade-in safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center" aria-label="close">
          <X weight="bold" className="w-6 h-6 text-white" />
        </button>
        <span className="text-white font-bold text-lg">share the moment</span>
        <div className="w-10" />
      </div>

      {/* Live preview */}
      <div className="flex-1 flex flex-col items-center justify-start px-4 overflow-y-auto">
        <div
          className="w-full max-w-xs aspect-[9/16] rounded-3xl overflow-hidden bg-white/5 border border-white/10 mt-2 flex items-center justify-center"
          style={{ minHeight: 360 }}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="card preview" className="w-full h-full object-cover" />
          ) : (
            <div className="text-center px-6">
              <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-2">right now at</p>
              <h2 className="text-2xl font-extrabold text-white">{restaurantName}</h2>
              <p className="text-white/40 text-xs mt-3">pick a vibe to preview your card</p>
            </div>
          )}
        </div>

        {/* Optional photo */}
        <div className="w-full max-w-sm mt-4">
          <p className="text-white/30 text-[10px] font-bold uppercase tracking-[0.22em] text-center mb-2">
            add a photo (optional)
          </p>
          <div className="flex gap-2">
            <button
              onClick={takePhoto}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/8 text-white/80 text-sm font-semibold active:scale-95 transition-all"
            >
              <CameraIcon weight="bold" className="w-4 h-4" />
              <span>camera</span>
            </button>
            <button
              onClick={pickPhoto}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/8 text-white/80 text-sm font-semibold active:scale-95 transition-all"
            >
              <ImageIcon weight="bold" className="w-4 h-4" />
              <span>library</span>
            </button>
            {photo && (
              <button
                onClick={() => setPhoto(null)}
                className="px-3 py-2.5 rounded-xl bg-white/8 text-white/60 text-xs font-semibold active:scale-95"
                aria-label="remove photo"
              >
                clear
              </button>
            )}
          </div>
        </div>

        {/* Status picker */}
        <div className="w-full max-w-sm mt-5">
          <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.22em] text-center mb-3">
            the vibe
          </p>
          <div className="grid grid-cols-4 gap-2">
            {PICKER_ORDER.map((s) => {
              const def = SHARE_STATUSES[s];
              const isActive = selectedStatus === s;
              return (
                <button
                  key={s}
                  onClick={() => { tap("light"); setSelectedStatus(s); }}
                  className={`py-2.5 px-1 rounded-2xl text-xs font-bold transition-all active:scale-95 ${
                    isActive ? "text-white shadow-lg scale-[1.04]" : "bg-white/8 text-white/65 hover:bg-white/12"
                  }`}
                  style={
                    isActive
                      ? { background: `linear-gradient(135deg, ${def.palette.bg1}, ${def.palette.accent})` }
                      : undefined
                  }
                  aria-pressed={isActive}
                >
                  <div className="text-base leading-none">{def.emoji}</div>
                  <div className="mt-1 text-[11px]">{def.label}</div>
                </button>
              );
            })}
          </div>
        </div>


      </div>

      {/* Bottom share */}
      <div className="px-4 pb-5 pt-3">
        <button
          onClick={handleShare}
          disabled={!selectedStatus || sharing}
          className={`w-full py-4 rounded-2xl font-extrabold text-base transition-all active:scale-[0.98] ${
            selectedStatus ? "bg-white text-black shadow-xl" : "bg-white/10 text-white/30"
          } ${sharing ? "opacity-60" : ""}`}
        >
          {sharing ? "preparing…" : selectedStatus ? `share ${SHARE_STATUSES[selectedStatus].emoji}` : "pick a vibe"}
        </button>
      </div>
    </div>
  );
}

// ─── Flow B: Verdict / "I ate this" ────────────────────────────────────────────

interface VerdictProps {
  restaurantName: string;
  dish: FoodItem;
  /** Legacy 3-rating, accepted for backward compat; mapped to a ShareStatus. */
  rating?: "slaps" | "mid" | "skip";
  onClose: () => void;
}

export function VerdictShareCard({ restaurantName, dish, rating, onClose }: VerdictProps) {
  // Legacy rating → status mapping (kept for back-compat with existing callers
  // that still pass the 3-rating prop). slaps→fire, mid→mid, skip→honest.
  const initialStatus: ShareStatus =
    rating === "slaps" ? "fire" : rating === "mid" ? "mid" : rating === "skip" ? "honest" : "fire";
  const [selectedStatus, setSelectedStatus] = useState<ShareStatus>(initialStatus);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!dish.imageUrl) return;
    let cancelled = false;
    let priorObjectUrl: string | null = null;
    (async () => {
      try {
        const blob = await renderVerdict({
          restaurantName,
          dishName: dish.name,
          dishImageUrl: dish.imageUrl!,
          status: selectedStatus,
        });
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        priorObjectUrl = url;
        setPreviewUrl(url);
      } catch (e) {
        if (!cancelled) console.error("[v2 verdict preview] failed:", fmtErr(e));
      }
    })();
    return () => {
      cancelled = true;
      if (priorObjectUrl) {
        setTimeout(() => URL.revokeObjectURL(priorObjectUrl!), 1000);
      }
    };
  }, [selectedStatus, restaurantName, dish.name, dish.imageUrl]);

  const handleShare = async () => {
    if (!v2Enabled()) {
      toast.error("v2 share disabled — set localStorage.yumzCardsV2 = '1' to re-enable", { duration: 4000 });
      return;
    }
    if (!dish.imageUrl) {
      toast.error("no dish image yet — wait for image to generate");
      return;
    }
    setSharing(true);
    try {
      const blob = await renderVerdict({
        restaurantName,
        dishName: dish.name,
        dishImageUrl: dish.imageUrl,
        status: selectedStatus,
      });
      const def = SHARE_STATUSES[selectedStatus];
      // Wave L1 — same tap-back-URL pattern as the check-in flow. Verdict
      // shares now carry a yumz.social link so recipients can hop into the
      // app instead of just receiving a photo. Wave M will add a per-dish
      // shared_verdict record with reactions like Wrapped.
      // Wave L2 — "Get Yumz" CTA framing same as the check-in flow.
      const shareText = `${dish.name} at ${restaurantName} ${def.emoji}\nGet Yumz: https://app.yumz.social`;
      await dispatchShare(blob, shareText, "yumz-verdict");
      toast.success("shared! 🎉");
      track("moment2_shared", {
        restaurant: restaurantName,
        dish: dish.name,
        status: selectedStatus,
        v2: true,
      });
      onClose();
    } catch (e) {
      if (isUserCancellation(e)) return;
      console.error("[v2 verdict share] failed:", fmtErr(e));
      toast.error("share failed — try again");
    } finally {
      setSharing(false);
    }
  };

  // Wave N — share straight into the Instagram Stories composer (native iOS).
  // Renders the same verdict card and hands it to the InstagramShare plugin;
  // openInstagramStories falls back to the system share sheet if IG isn't
  // installed or the native open fails, so this never dead-ends.
  const handleShareStories = async () => {
    if (!dish.imageUrl) {
      toast.error("no dish image yet — wait for image to generate");
      return;
    }
    setSharing(true);
    try {
      const blob = await renderVerdict({
        restaurantName,
        dishName: dish.name,
        dishImageUrl: dish.imageUrl,
        status: selectedStatus,
      });
      const file = new File([blob], "yumz-verdict.jpg", { type: "image/jpeg" });
      await openInstagramStories(file);
      track("moment2_shared", { restaurant: restaurantName, dish: dish.name, status: selectedStatus, v2: true, target: "instagram-story" });
      onClose();
    } catch (e) {
      if (isUserCancellation(e)) return;
      console.error("[v2 verdict IG-stories share] failed:", fmtErr(e));
      toast.error("instagram share failed — try the share button");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col page-fade-in safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center" aria-label="close">
          <X weight="bold" className="w-6 h-6 text-white" />
        </button>
        <span className="text-white font-bold text-lg">the verdict</span>
        <div className="w-10" />
      </div>

      {/* Live preview */}
      <div className="flex-1 flex flex-col items-center justify-start px-4 overflow-y-auto">
        <div
          className="w-full max-w-xs aspect-[9/16] rounded-3xl overflow-hidden bg-white/5 border border-white/10 mt-2 flex items-center justify-center"
          style={{ minHeight: 360 }}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="verdict preview" className="w-full h-full object-cover" />
          ) : dish.imageUrl ? (
            <img src={dish.imageUrl} alt={dish.name} className="w-full h-full object-cover opacity-50" />
          ) : (
            <div className="text-center px-6">
              <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-2">verdict</p>
              <h2 className="text-2xl font-extrabold text-white">{dish.name}</h2>
              <p className="text-white/40 text-xs mt-3">waiting for dish image to generate…</p>
            </div>
          )}
        </div>

        <div className="w-full max-w-sm mt-5">
          <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.22em] text-center mb-3">
            the take
          </p>
          <div className="grid grid-cols-4 gap-2">
            {PICKER_ORDER.map((s) => {
              const def = SHARE_STATUSES[s];
              const isActive = selectedStatus === s;
              return (
                <button
                  key={s}
                  onClick={() => { tap("light"); setSelectedStatus(s); }}
                  className={`py-2.5 px-1 rounded-2xl text-xs font-bold transition-all active:scale-95 ${
                    isActive ? "text-white shadow-lg scale-[1.04]" : "bg-white/8 text-white/65 hover:bg-white/12"
                  }`}
                  style={
                    isActive
                      ? { background: `linear-gradient(135deg, ${def.palette.bg1}, ${def.palette.accent})` }
                      : undefined
                  }
                  aria-pressed={isActive}
                >
                  <div className="text-base leading-none">{def.emoji}</div>
                  <div className="mt-1 text-[11px]">{def.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom share */}
      <div className="px-4 pb-5 pt-3 space-y-2">
        <button
          onClick={handleShare}
          disabled={!dish.imageUrl || sharing}
          className={`w-full py-4 rounded-2xl font-extrabold text-base transition-all active:scale-[0.98] ${
            dish.imageUrl ? "bg-white text-black shadow-xl" : "bg-white/10 text-white/30"
          } ${sharing ? "opacity-60" : ""}`}
        >
          {sharing ? "preparing…" : `share ${SHARE_STATUSES[selectedStatus].emoji}`}
        </button>
        {isNativeIOS && (
          <button
            onClick={handleShareStories}
            disabled={!dish.imageUrl || sharing}
            className={`w-full py-3 rounded-2xl font-bold text-sm transition-all active:scale-[0.98] ${
              dish.imageUrl
                ? "bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white"
                : "bg-white/10 text-white/30"
            } ${sharing ? "opacity-60" : ""}`}
          >
            instagram stories
          </button>
        )}
      </div>
    </div>
  );
}
